import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaLightbulb, FaRocket, FaBriefcase } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import styles from './ScrollIndicator.module.scss';

const ScrollIndicator = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('about');
  const [scrollProgress, setScrollProgress] = useState(0);

  const sections = [
    { id: 'about', label: t('nav.about'), icon: <FaUser /> },
    { id: 'expertise', label: t('nav.expertise'), icon: <FaLightbulb /> },
    { id: 'projects', label: t('nav.projects'), icon: <FaRocket /> },
    { id: 'experience', label: t('nav.experience'), icon: <FaBriefcase /> }
  ];

  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.pageYOffset / totalHeight) * 100;
      setScrollProgress(progress);

      // Determine active section with more precise calculations
      const viewportHeight = window.innerHeight;
      const scrollTop = window.pageYOffset;
      const trigger = viewportHeight * 0.3; // Adjust this value to change when sections become active

      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= trigger && rect.bottom >= trigger) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    setTimeout(handleScroll, 100);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 0;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className={styles.scrollIndicator}>
      <div className={styles.progressBarContainer}>
        <motion.div 
          className={styles.progressBar}
          style={{ height: `${scrollProgress}%` }}
          initial={{ height: '0%' }}
          animate={{ height: `${scrollProgress}%` }}
          transition={{ duration: 0.1 }}
        />
      </div>
      
      <div className={styles.sectionMarkers}>
        {sections.map((section, index) => (
          <motion.button
            key={section.id}
            className={`${styles.marker} ${activeSection === section.id ? styles.active : ''}`}
            onClick={() => scrollToSection(section.id)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            title={section.label}
          >
            <span className={styles.icon}>{section.icon}</span>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

export default ScrollIndicator;